import React, { createContext, useRef, useEffect, useContext, useMemo } from 'react';
import Dialog from '@material-ui/core/Dialog';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import DialogTitle from '@material-ui/core/DialogTitle';
import Box from '@material-ui/core/Box';
import DialogContent from '@material-ui/core/DialogContent';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
function __rest(s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
}

function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }

  return obj;
}

var context = /*#__PURE__*/createContext({
  onClose: function onClose() {
    return undefined;
  },
  headerText: ''
});
var useCloseDialog = function useCloseDialog() {
  var _useContext = useContext(context),
      onClose = _useContext.onClose;

  return onClose;
};
var useHeaderText = function useHeaderText() {
  var _useContext2 = useContext(context),
      headerText = _useContext2.headerText;

  return headerText;
};
var id = Math.floor(Math.random() * 1000);
var useId = function useId() {
  var prefix = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : 'scoir-id';
  var refId = useRef();

  if (refId.current === undefined) {
    refId.current = id;
    id++;
  }

  return "".concat(prefix, "-").concat(refId.current);
};
function useMemoizedCallback(fn) {
  var fnRef = useRef(fn);
  useEffect(function () {
    fnRef.current = fn;
  }, [fn]);
  return fnRef.current;
}

var useStyles$3 = makeStyles(function (theme) {
  return {
    dialogPaperFullWidth: _defineProperty({}, theme.breakpoints.down('xs'), {
      width: "calc(100% - ".concat(theme.spacing(2), "px)")
    }),
    paperScrollPaper: _defineProperty({}, theme.breakpoints.down('xs'), {
      maxHeight: "calc(100% - ".concat(theme.spacing(2), "px)")
    }),
    dialogPaper: _defineProperty({}, theme.breakpoints.down('xs'), {
      margin: theme.spacing(1)
    })
  };
});

var ModalDialog = function ModalDialog(_a) {
  var _a$open = _a.open,
      open = _a$open === void 0 ? false : _a$open,
      _a$enforceFocus = _a.enforceFocus,
      enforceFocus = _a$enforceFocus === void 0 ? true : _a$enforceFocus,
      onClose = _a.onClose,
      headerId = _a.headerId,
      children = _a.children,
      rest = __rest(_a, ["open", "enforceFocus", "onClose", "headerId", "children"]);

  var classes = useStyles$3();
  return /*#__PURE__*/React.createElement(Dialog, Object.assign({
    fullWidth: true,
    open: open,
    scroll: "paper",
    maxWidth: "sm",
    onClose: onClose,
    "aria-labelledby": headerId,
    disableEnforceFocus: !enforceFocus,
    classes: {
      paperFullWidth: classes.dialogPaperFullWidth,
      paperScrollPaper: classes.paperScrollPaper,
      paper: classes.dialogPaper
    }
  }, rest), children);
};

var CloseableDialog = function CloseableDialog(props) {
  var onClose = useCloseDialog();
  return /*#__PURE__*/React.createElement(ModalDialog, Object.assign({}, props, {
    onClose: onClose
  }));
};

var useStyles$2 = makeStyles(function (theme) {
  return {
    header: {
      padding: theme.spacing(2, 3),
      position: 'relative'
    },
    icon: {
      top: theme.spacing(1),
      right: theme.spacing(1),
      position: 'absolute'
    }
  };
});

var Header = function Header(_a) {
  var className = _a.className,
      headerId = _a.headerId,
      children = _a.children,
      rest = __rest(_a, ["className", "headerId", "children"]);

  var classes = useStyles$2();
  var headerText = useHeaderText();
  return /*#__PURE__*/React.createElement(DialogTitle, Object.assign({
    className: [classes.header, className].join(' '),
    disableTypography: true
  }, rest), /*#__PURE__*/React.createElement(Box, {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  }, /*#__PURE__*/React.createElement(Typography, {
    id: headerId,
    display: "inline",
    component: "h2",
    variant: "h5"
  }, headerText), children));
};

var useStyles$1 = makeStyles(function (theme) {
  return {
    body: {
      padding: theme.spacing(3)
    },
    withBackgroundColor: {
      // theme.palette.pageBackground.main does not play nicely with eslint & TS
      background: '#f5f8fa'
    }
  };
});

var Body$1 = function Body(_a) {
  var className = _a.className,
      withBackgroundColor = _a.withBackgroundColor,
      children = _a.children,
      rest = __rest(_a, ["className", "withBackgroundColor", "children"]);

  var classes = useStyles$1();
  return /*#__PURE__*/React.createElement(DialogContent, Object.assign({}, rest, {
    dividers: true,
    className: [className, classes.body, withBackgroundColor && classes.withBackgroundColor].filter(Boolean).join(' ')
  }), children);
};

var useStyles = makeStyles(function (theme) {
  return {
    footer: {
      padding: theme.spacing(2, 3),
      position: 'relative'
    }
  };
});

var Footer$1 = function Footer(_a) {
  var className = _a.className,
      children = _a.children,
      rest = __rest(_a, ["className", "children"]);

  var classes = useStyles();
  return /*#__PURE__*/React.createElement(DialogActions, Object.assign({
    className: [classes.footer, className].join(' ')
  }, rest), children);
};

var CloseButton$1 = function CloseButton(props) {
  var onClose = useCloseDialog();
  return /*#__PURE__*/React.createElement(Button, Object.assign({}, props, {
    color: "primary",
    size: "large",
    onClick: onClose
  }));
};

var PrimaryActionButton$1 = function PrimaryActionButton(props) {
  return /*#__PURE__*/React.createElement(Button, Object.assign({}, props, {
    variant: "contained",
    color: "primary",
    size: "large"
  }));
};

var SecondaryActionButton = function SecondaryActionButton(props) {
  return /*#__PURE__*/React.createElement(Button, Object.assign({}, props, {
    variant: "outlined",
    color: "primary",
    size: "large"
  }));
};

Footer$1.CloseButton = CloseButton$1;
Footer$1.PrimaryActionButton = PrimaryActionButton$1;
Footer$1.SecondaryActionButton = SecondaryActionButton;

var DismissibleDialogBody = Body$1;
var DismissibleDialogFooter = Footer$1;

var DismissibleDialog = function DismissibleDialog(_a) {
  var onClose = _a.onClose,
      headerText = _a.headerText,
      headerChildren = _a.headerChildren,
      headerClassName = _a.headerClassName,
      children = _a.children,
      rest = __rest(_a, ["onClose", "headerText", "headerChildren", "headerClassName", "children"]);

  var closeFn = useMemoizedCallback(onClose);
  var contextValue = useMemo(function () {
    return {
      onClose: closeFn,
      headerText: headerText
    };
  }, [headerText, closeFn]);
  var headerId = useId('dismissable-dialog');
  return /*#__PURE__*/React.createElement(context.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(CloseableDialog, Object.assign({
    headerId: headerId
  }, rest), /*#__PURE__*/React.createElement(Header, {
    headerId: headerId,
    className: headerClassName
  }, headerChildren), children));
};

DismissibleDialog.Body = DismissibleDialogBody;
DismissibleDialog.Footer = DismissibleDialogFooter;

var Body = DismissibleDialog.Body,
    Footer = DismissibleDialog.Footer;
var CloseButton = Footer.CloseButton,
    PrimaryActionButton = Footer.PrimaryActionButton;

var ConfirmationDialog = function ConfirmationDialog(_a) {
  var _a$open = _a.open,
      open = _a$open === void 0 ? false : _a$open,
      _a$cancellationText = _a.cancellationText,
      cancellationText = _a$cancellationText === void 0 ? 'Cancel' : _a$cancellationText,
      _a$confirmationText = _a.confirmationText,
      confirmationText = _a$confirmationText === void 0 ? 'Confirm' : _a$confirmationText,
      _a$headerText = _a.headerText,
      headerText = _a$headerText === void 0 ? 'Confirm Action' : _a$headerText,
      headerChildren = _a.headerChildren,
      children = _a.children,
      onClose = _a.onClose,
      onConfirm = _a.onConfirm,
      rest = __rest(_a, ["open", "cancellationText", "confirmationText", "headerText", "headerChildren", "children", "onClose", "onConfirm"]);

  return /*#__PURE__*/React.createElement(DismissibleDialog, Object.assign({}, rest, {
    open: open,
    onClose: onClose,
    headerText: headerText,
    headerChildren: headerChildren
  }), /*#__PURE__*/React.createElement(Body, null, children), /*#__PURE__*/React.createElement(Footer, null, /*#__PURE__*/React.createElement(CloseButton, null, cancellationText), /*#__PURE__*/React.createElement(PrimaryActionButton, {
    onClick: onConfirm
  }, confirmationText)));
};

var MultiSectionDialogBody = function MultiSectionDialogBody(props) {
  return /*#__PURE__*/React.createElement(Body$1, Object.assign({}, props, {
    withBackgroundColor: true
  }));
};

var MultiSectionDialogFooter = Footer$1;

var MultiSectionDialog = function MultiSectionDialog(_a) {
  var onClose = _a.onClose,
      headerText = _a.headerText,
      headerClassName = _a.headerClassName,
      headerChildren = _a.headerChildren,
      children = _a.children,
      rest = __rest(_a, ["onClose", "headerText", "headerClassName", "headerChildren", "children"]);

  var closeFn = useMemoizedCallback(onClose);
  var headerId = useId('multi-section-dialog');
  var contextValue = useMemo(function () {
    return {
      onClose: closeFn,
      headerText: headerText
    };
  }, [headerText, closeFn]);
  return /*#__PURE__*/React.createElement(context.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(ModalDialog, Object.assign({
    headerId: headerId
  }, rest), /*#__PURE__*/React.createElement(Header, {
    headerId: headerId,
    className: headerClassName
  }, headerChildren), children));
};

MultiSectionDialog.Body = MultiSectionDialogBody;
MultiSectionDialog.Footer = MultiSectionDialogFooter;

var NonDismissibleDialogBody = Body$1;
var NonDismissibleDialogFooter = Footer$1;

var NonDismissibleDialog = function NonDismissibleDialog(_a) {
  var onClose = _a.onClose,
      headerText = _a.headerText,
      headerClassName = _a.headerClassName,
      headerChildren = _a.headerChildren,
      children = _a.children,
      rest = __rest(_a, ["onClose", "headerText", "headerClassName", "headerChildren", "children"]);

  var closeFn = useMemoizedCallback(onClose);
  var headerId = useId('non-dismissable-dialog');
  var contextValue = useMemo(function () {
    return {
      onClose: closeFn,
      headerText: headerText
    };
  }, [headerText, closeFn]);
  return /*#__PURE__*/React.createElement(context.Provider, {
    value: contextValue
  }, /*#__PURE__*/React.createElement(ModalDialog, Object.assign({}, rest, {
    headerId: headerId
  }), /*#__PURE__*/React.createElement(Header, {
    headerId: headerId,
    className: headerClassName
  }, headerChildren), children));
};

NonDismissibleDialog.Body = NonDismissibleDialogBody;
NonDismissibleDialog.Footer = NonDismissibleDialogFooter;

export { ConfirmationDialog, DismissibleDialog, MultiSectionDialog, NonDismissibleDialog };
//# sourceMappingURL=index.module.js.map
